import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useUsers, useSuspendUser, useDeleteUser } from '../../hooks/useUsers'
import { usersAPI, type UserWithStats } from '../../api/users'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

// ─── Sub-components ───────────────────────────────────────────────────────────

function AvatarBadge({ name }: { name: string }) {
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      background: '#E1F5EE', color: '#0F6E56',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 13, flexShrink: 0,
    }}>
      {name.trim().slice(0, 2).toUpperCase()}
    </div>
  )
}

export function ExpediteurStatusBadge({ suspended, verified }: { suspended?: boolean; verified?: boolean }) {
  if (suspended) return (
    <span style={{ background: '#FEF2F2', color: '#EF4444', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
      Suspendu
    </span>
  )
  if (verified) return (
    <span style={{ background: '#F0FDF4', color: '#10B981', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
      Vérifié
    </span>
  )
  return (
    <span style={{ background: '#FFFBEB', color: '#F59E0B', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
      En attente
    </span>
  )
}

export function PlanBadge({ plan }: { plan: string }) {
  const cfg: Record<string, { bg: string; color: string }> = {
    free: { bg: '#F3F4F6', color: '#6B7280' },
    pro: { bg: '#EFF6FF', color: '#3B82F6' },
    business: { bg: '#F0FDF4', color: '#10B981' },
  }
  const c = cfg[plan] ?? cfg.free
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
      {plan.charAt(0).toUpperCase() + plan.slice(1)}
    </span>
  )
}

function ActionBtn({ title, icon, onClick, color = '#374151' }: {
  title: string; icon: string; onClick: () => void; color?: string
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: 'none', border: '1px solid #E5E7EB', borderRadius: 6,
        padding: '4px 8px', cursor: 'pointer', fontSize: 13, color,
      }}
    >
      {icon}
    </button>
  )
}

function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div style={{ padding: '16px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: '1px solid #F9FAFB' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F3F4F6' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
            <div style={{ height: 12, width: '40%', borderRadius: 4, background: '#F3F4F6' }} />
            <div style={{ height: 10, width: '25%', borderRadius: 4, background: '#F3F4F6' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Modal formulaire ─────────────────────────────────────────────────────────

interface FormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  user?: UserWithStats
}

function ExpediteurFormModal({ isOpen, onClose, onSuccess, user }: FormModalProps) {
  const isEdit = !!user
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    companyName: user?.company?.name ?? '',
    plan: user?.plan ?? 'free',
    isVerified: user?.isVerified ?? false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: user?.name ?? '',
        email: user?.email ?? '',
        phone: user?.phone ?? '',
        companyName: user?.company?.name ?? '',
        plan: user?.plan ?? 'free',
        isVerified: user?.isVerified ?? false,
      })
      setErrors({})
    }
  }, [isOpen, user])

  const set = (field: string, value: string | boolean) =>
    setForm((f) => ({ ...f, [field]: value }))

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nom obligatoire'
    if (!form.phone.trim()) e.phone = 'Téléphone obligatoire'
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setLoading(true)
    try {
      const phone = form.phone.startsWith('+') ? form.phone : `+216${form.phone}`
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        phone,
        company: { name: form.companyName.trim() || undefined },
        plan: form.plan,
        isVerified: form.isVerified,
      }
      if (form.email.trim()) payload.email = form.email.trim()

      if (isEdit) {
        await usersAPI.update(user!._id, payload)
        toast.success('Profil mis à jour')
      } else {
        await usersAPI.createExpediteur(payload)
        toast.success('Expéditeur créé')
      }
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(msg ?? 'Erreur lors de l\'enregistrement')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }
  const errorStyle: React.CSSProperties = { fontSize: 11, color: '#EF4444' }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32, width: 480, maxWidth: '90vw',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1117', marginBottom: 24 }}>
          {isEdit ? 'Modifier l\'expéditeur' : 'Nouvel expéditeur'}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Nom complet *</label>
            <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ahmed Ben Ali" />
            {errors.name && <span style={errorStyle}>{errors.name}</span>}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Téléphone *</label>
            <input style={inputStyle} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+21612345678" />
            {errors.phone && <span style={errorStyle}>{errors.phone}</span>}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contact@entreprise.tn" />
            {errors.email && <span style={errorStyle}>{errors.email}</span>}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Entreprise</label>
            <input style={inputStyle} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="Nom de l'entreprise" />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Plan</label>
            <select style={inputStyle} value={form.plan} onChange={(e) => set('plan', e.target.value)}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.isVerified}
              onChange={(e) => set('isVerified', e.target.checked)}
            />
            <span style={{ fontSize: 13, color: '#374151' }}>Compte vérifié</span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button
            onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '9px 18px', borderRadius: 8, border: 'none',
              background: loading ? '#9CA3AF' : '#1D9E75', color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            {loading ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminExpediteurs() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | 'suspended'>('all')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1) }, 500)
    return () => clearTimeout(t)
  }, [search])

  const { data, isLoading, isFetching } = useUsers({
    role: 'expediteur',
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    page,
    limit: 20,
  })

  const suspendUser = useSuspendUser()
  const deleteUser = useDeleteUser()

  const handleSuspend = (u: UserWithStats) => {
    if (u.isSuspended) {
      suspendUser.mutate({ id: u._id, suspended: false })
    } else {
      const reason = window.prompt('Motif de suspension :')
      if (reason !== null) {
        suspendUser.mutate({ id: u._id, suspended: true, reason })
      }
    }
  }

  const handleDelete = (u: UserWithStats) => {
    if (window.confirm(`Supprimer "${u.name}" ? Cette action est irréversible.`)) {
      deleteUser.mutate(u._id)
    }
  }

  const thStyle: React.CSSProperties = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
    color: '#6B7280', fontSize: 11, letterSpacing: '0.04em',
    textTransform: 'uppercase', background: '#F9FAFB',
    borderBottom: '2px solid #F3F4F6',
  }
  const tdStyle: React.CSSProperties = { padding: '12px', verticalAlign: 'middle' }

  const tabStyle: React.CSSProperties = {
    padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
    background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 6,
  }
  const activeTabStyle: React.CSSProperties = {
    background: '#0D1117', color: '#fff', border: '1px solid #0D1117',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 22, color: '#0D1117', margin: 0 }}>
            Expéditeurs
          </h1>
          <p style={{ color: '#9CA3AF', fontSize: 13, marginTop: 4 }}>Gestion des comptes expéditeurs</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: '#1D9E75', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}
        >
          + Nouvel expéditeur
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: '#fff', padding: '14px 16px', borderRadius: 12, border: '1px solid #F3F4F6' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'all', label: 'Tous', count: data?.pagination.total },
            { key: 'active', label: 'Actifs' },
            { key: 'suspended', label: 'Suspendus' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setStatus(tab.key as typeof status); setPage(1) }}
              style={{ ...tabStyle, ...(status === tab.key ? activeTabStyle : {}) }}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span style={{
                  background: status === tab.key ? 'rgba(255,255,255,0.2)' : '#F3F4F6',
                  color: status === tab.key ? '#fff' : '#6B7280',
                  borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, email, téléphone, entreprise…"
            style={{
              padding: '8px 32px 8px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
              fontSize: 13, width: 260, outline: 'none',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Fetching indicator */}
      {isFetching && !isLoading && (
        <div style={{ height: 2, background: '#1D9E75', borderRadius: 2, marginBottom: -16 }} />
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #F3F4F6', overflow: 'hidden' }}>
        {isLoading ? (
          <TableSkeleton rows={8} />
        ) : !data?.users.length ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
            {debouncedSearch ? `Aucun résultat pour "${debouncedSearch}"` : 'Aucun expéditeur trouvé'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['ENTREPRISE / CONTACT', 'CONTACT', 'PLAN', 'COLIS', 'STATUT', 'INSCRIT LE', 'ACTIONS'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u._id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <AvatarBadge name={u.company?.name ?? u.name} />
                        <div>
                          <div style={{ fontWeight: 700, color: '#0D1117' }}>
                            {u.company?.name ?? u.name}
                          </div>
                          {u.company?.name && (
                            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{u.name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ color: '#374151' }}>{u.email ?? '—'}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{u.phone}</div>
                    </td>
                    <td style={tdStyle}><PlanBadge plan={u.plan ?? 'free'} /></td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 700, color: '#0D1117' }}>{u.parcelCount ?? 0}</span>
                    </td>
                    <td style={tdStyle}>
                      <ExpediteurStatusBadge suspended={u.isSuspended} verified={u.isVerified} />
                    </td>
                    <td style={{ ...tdStyle, color: '#9CA3AF', fontSize: 12 }}>{fmtDate(u.createdAt)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <ActionBtn title="Voir le détail" icon="👁️" onClick={() => navigate(`/admin/expediteurs/${u._id}`)} />
                        <ActionBtn title="Modifier" icon="✏️" onClick={() => navigate(`/admin/expediteurs/${u._id}`)} />
                        <ActionBtn
                          title={u.isSuspended ? 'Réactiver' : 'Suspendre'}
                          icon={u.isSuspended ? '✅' : '🚫'}
                          onClick={() => handleSuspend(u)}
                          color={u.isSuspended ? '#10B981' : '#EF4444'}
                        />
                        <ActionBtn
                          title="Supprimer"
                          icon="🗑️"
                          onClick={() => handleDelete(u)}
                          color="#EF4444"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(data?.pagination.totalPages ?? 0) > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, opacity: page === 1 ? 0.5 : 1 }}
          >
            ← Précédent
          </button>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            Page {page} / {data!.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data!.pagination.totalPages, p + 1))}
            disabled={page === data!.pagination.totalPages}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: page === data!.pagination.totalPages ? 'not-allowed' : 'pointer', fontSize: 13, opacity: page === data!.pagination.totalPages ? 0.5 : 1 }}
          >
            Suivant →
          </button>
        </div>
      )}

      {/* Create modal */}
      <ExpediteurFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
      />
    </div>
  )
}
